import React, { useEffect, useState } from "react";
import DaterangePicker from "../../common/Daterangepicker";
import formatDateAndTime from "../../common/formatDateAndTime";
import { useSelector } from "react-redux";
import DashboardSkeleton from "../../common/DashboardSkeleton";
import toast from "react-hot-toast";

const Bookingledger = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  let token = localStorage.getItem("token");
  const isSystemUserList = useSelector(
    (state) => state?.isSystemUser?.isSysTemUserData?.entities?.rows,
  );
  const cities = useSelector((state) => state?.cities?.citiesData);
  const [openRow, setOpenRow] = useState(null);
  const [allLedgerDetails, setBookingLedgerDetails] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [selectSearchValues, setSelectSearchValues] = useState(null);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  oneMonthAgo.setHours(0, 0, 0, 0);
  const [selectionRange, setSelectionRange] = useState({
    startDate: oneMonthAgo,
    endDate: today,
    key: "selection",
  });
  const [cityId, setCityId] = useState("1");
  const [hubId, setHubId] = useState("1");
  const [getAllHubsListData, setGetAllHubsListData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const getAllHubsList = async (id) => {
    try {
      const response = await fetch(`${apiUrl}/api/hubs?cityId=${id}`);
      const data = await response.json();
      if (data?.statusCode === "0100")
        setGetAllHubsListData(data?.entities?.rows);
    } catch (error) {
      console.error("Error fetching City Names:", error);
    }
  };

  const toggleDrawer = (id) => {
    setOpenRow(openRow === id ? null : id);
  };

  function formatDateForApi(date) {
    const pad = (n) => (n < 10 ? "0" + n : n);
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  const getAllLEdgerDetails = async (
    customPage = page,
    customRowsPerPage = rowsPerPage,
    selectedCityId = cityId,
    selectedHubId = hubId,
    selectedRange = selectionRange,
  ) => {
    const formattedStartDate = formatDateForApi(selectedRange?.startDate);
    const formattedEndDate = formatDateForApi(selectedRange?.endDate);
    const offset = (customPage ?? 0) * (customRowsPerPage ?? rowsPerPage);
    const queryParts = [];
    if (selectedCityId) queryParts.push(`cityId=${selectedCityId}`);
    if (selectedHubId) queryParts.push(`hubId=${selectedHubId}`);
    const queryString = queryParts.length ? `&${queryParts.join("&")}` : "";
    setLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}/api/dashboardreports/reconciled-booking-ledger?filterType=createdAt&startDate=${formattedStartDate}&endDate=${formattedEndDate}${queryString}&offset=${offset}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );
      const data = await response.json();
      if (data?.statusCode === "0100") {
        setLoading(false);
        setBookingLedgerDetails(data?.dataValues);
        setTotalCount(data?.entities?.count || 0);
        setPage(0);
      } else {
        setLoading(false);
        toast.error("Failed to fetch booking ledger details" , data?.msg);
      }
      return data;
    } catch (error) {
      return console.error("Error fetching Hub Names:", error);
    }
  };

  useEffect(() => {
    getAllLEdgerDetails(0);
  }, []);

  useEffect(() => {
    if (cityId) {
      getAllHubsList(cityId);
    }
  }, [cityId]);

  const ledgerRecords = allLedgerDetails?.reconciledBookingLedger || [];
  const totalRecords = ledgerRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));
  const displayedRecords = ledgerRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  const startRecord = totalRecords === 0 ? 0 : page * rowsPerPage + 1;
  const endRecord = Math.min((page + 1) * rowsPerPage, totalRecords);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [page, totalPages]);

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-white rounded-xl shadow-sm p-6 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">
            Reconciled Booking
          </h1>
        </div>

        <div className="flex gap-2">
          <div className="">
            <DaterangePicker
              selectionRange={selectionRange}
              setSelectionRange={setSelectionRange}
            />
          </div>

          <select
            value={cityId}
            onChange={(e) => setCityId(e.target.value)}
            className="h-10 rounded-lg border border-gray-300 px-3 text-sm 
                   bg-white focus:outline-none focus:ring-2 
                   focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Selecr City Option</option>
            {Object.entries(cities).map(([key, value]) => (
              <option value={key}>{value}</option>
            ))}
          </select>

          <select
            value={hubId}
            onChange={(e) => setHubId(e.target.value)}
            className="h-10 rounded-lg border border-gray-300 px-3 text-sm 
                   bg-white focus:outline-none focus:ring-2 
                   focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Selecr Hub Option</option>
            {getAllHubsListData &&
              getAllHubsListData.map((item, index) => (
                <option key={index} value={item?.id}>
                  {item?.hubName}
                </option>
              ))}
          </select>

          <button
            onClick={() => {
              getAllLEdgerDetails();
            }}
            className="p-2 bg-blue-400"
          >
            Search
          </button>
          <button
            onClick={() => {
              setCityId("1");
              setHubId("1");
              const resetRange = {
                startDate: oneMonthAgo,
                endDate: today,
                key: "selection",
              };
              setSelectionRange(resetRange);
              getAllLEdgerDetails(0, rowsPerPage, "1", "1", resetRange);
            }}
            className="p-2 bg-red-400"
          >
            Clear
          </button>
        </div>
      </div>

      
      {
        loading ? (
          <DashboardSkeleton/>
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {/* Card 1 */}
        <div className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg p-5">
          <p className="text-sm uppercase tracking-wider opacity-90">
            Total Active Records
          </p>
          <h2 id="kpiCount" className="text-3xl font-bold mt-3">
            {allLedgerDetails?.totalRecords}
          </h2>
        </div>

        {/* Card 2 */}
        {/* <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
          <p className="text-sm uppercase tracking-wider text-gray-500">
            Gross Customer Receivables
          </p>
          <h2 id="kpiGross" className="text-3xl font-bold text-gray-800 mt-3">
            ₹0.00
          </h2>
        </div> */}

        {/* Card 3 */}
        {/* <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
          <p className="text-sm uppercase tracking-wider text-gray-500">
            Ad-Hoc Penalty Pool
          </p>
          <h2
            id="kpiPenalties"
            className="text-3xl font-bold text-gray-800 mt-3"
          >
            ₹0.00
          </h2>
        </div> */}

        {/* Card 4 */}
        <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
          <p className="text-sm uppercase tracking-wider text-gray-500">
            Security Deposit
          </p>
          <h2 id="kpiEscrow" className="text-3xl font-bold text-gray-800 mt-3">
            ₹ {allLedgerDetails?.escrowReservesSecurityDeposit}
          </h2>
        </div>
      </div>      

      <div className="bg-white rounded-xl shadow-md overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200">
            <tr className="text-xs uppercase tracking-wider text-gray-700">
              <th className="px-6 py-4 text-left font-semibold">
                Booking Code
              </th>

              {/* <th className="px-6 py-4 text-left font-semibold">
                Vehicle Detail
              </th> */}

              {/* <th className="px-6 py-4 text-left font-semibold">
                Customer Workspace
              </th> */}

              <th className="px-6 py-4 text-left font-semibold">
                Operational Timeline
              </th>

              <th className="px-6 py-4 text-left font-semibold">
                Financial Loadout
              </th>

              <th className="px-6 py-4 text-left font-semibold">
                Attached Ledgers
              </th>

              <th className="px-6 py-4 text-center font-semibold">Status</th>
            </tr>
          </thead>

          <tbody>
            {displayedRecords.map((record, index) => (
                <React.Fragment key={record.id}>
                  {/* Main Row */}
                  <tr
                    onClick={() => toggleDrawer(record.id)}
                    className={`cursor-pointer transition-all duration-300 hover:bg-blue-50 ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <td className="px-6 py-5">
                      <div
                        className="font-semibold text-gray-900 hover:text-red-500"
                        onClick={() => {
                          const navigate = `/viewallbookings?bookingid=${record?.bookingCode}&display=true`;
                          window.open(navigate, "_blank");
                        }}
                      >
                        {record.bookingCode}
                      </div>

                      <div className="text-xs text-gray-500">
                        Source : {record.source}
                      </div>

                      <div className="text-xs text-gray-500">
                        Original Source : {record.originalSource}
                      </div>

                      <div className="text-xs text-gray-500">
                        Generated On :{" "}
                        {formatDateAndTime(record.bookingGeneratedOn)}
                      </div>
                    </td>

                    {/* <td className="px-6 py-5">
                      <div className="font-medium text-gray-800">
                        {record.vName || "-"}
                      </div>

                      <div className="text-sm text-gray-500">
                        {record.vReg || "-"}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="font-medium">{record.cName}</div>

                      <div className="text-sm text-gray-500">{record.cEmail}</div>
                    </td> */}

                    <td className="px-6 py-5">
                      <div className="text-sm text-gray-500">
                        From : {formatDateAndTime(record.bookFrom)}
                      </div>

                      <div className="text-sm text-gray-500">
                        To : {formatDateAndTime(record.bookTo)}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                    <div className="font-bold text-green-600 text-lg">
                      ₹{record.bookingRevenue}
                    </div>
                  </td>

                  <td className="px-6 py-5">
                    <span className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-semibold text-indigo-700">
                      {record.transactionList.length} Transactions
                    </span>
                  </td>

                  <td className="px-6 py-5 text-center">
                    <span
                      className={`inline-flex rounded-full px-4 py-1 text-xs font-bold
                  ${
                    record.status === "Trip Completed"
                      ? "bg-green-100 text-green-700"
                      : record.status === "Booking Closed"
                        ? "bg-yellow-100 text-yellow-700"
                        : record.status === "Enroute"
                          ? "bg-red-100 text-red-700"
                          : "bg-blue-100 text-blue-700"
                  }`}
                    >
                      {record.bookingStatus}
                    </span>
                  </td>
                </tr>

                {openRow === record.id && (
                  <tr>
                    <td colSpan={7} className="bg-gray-50 p-6">
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div className="bg-white rounded-2xl shadow-md border p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-5 border-b pb-3">
                              Base Billing Details
                            </h2>

                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span>
                                  Base Rent (Week Day + Week End Rent)
                                </span>
                                <span className="font-semibold">
                                  ₹{record.weekdayRent + record?.weekendRent}
                                </span>
                              </div>

                              <div className="flex justify-between">
                                <span>Discount</span>
                                <span>₹{record.discountAmount}</span>
                              </div>

                              <div className="flex justify-between">
                                <span>Tax</span>
                                <span>₹{record.taxAmount}</span>
                              </div>

                              <div className="flex justify-between">
                                <span>Delivery Charge</span>
                                <span>₹{record.deliveryCharges}</span>
                              </div>

                              <div className="flex justify-between">
                                <span>Pickup Charge</span>
                                <span>₹{record.pickupCharges}</span>
                              </div>

                              <div className="flex justify-between">
                                <span>Security Deposit</span>
                                <span>₹{record.securityDeposit}</span>
                              </div>

                              <hr />

                              <div className="flex justify-between text-lg font-bold text-green-600">
                                <span>Total Amount</span>
                                <span>₹{record.bookingRevenue}</span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white rounded-2xl shadow-md border p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-5 border-b pb-3">
                              Other Details
                            </h2>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">
                                  Total Transaction Amount
                                </p>
                                <p className="font-semibold">
                                  {record.totalTransactionAmount}
                                </p>
                              </div>

                              <div>
                                <p className="text-gray-500">
                                  Refund Data Count
                                </p>
                                <p className="font-semibold">
                                  {record.refundDataCount}
                                </p>
                              </div>

                              <div>
                                <p className="text-gray-500">Charges Count</p>
                                <p className="font-semibold">
                                  {record.chargesCount}
                                </p>
                              </div>

                              <div>
                                <p className="text-gray-500">
                                  Total Charges Amount
                                </p>
                                <p>{record?.totalChargesAmount}</p>
                              </div>

                              <div>
                                <p className="text-gray-500">
                                  Refunds Line Items Count
                                </p>
                                <p>{record?.refundsLineItemsCount}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Transactions */}
                        <div className="bg-white rounded-2xl shadow-md border p-6">
                          <h2 className="text-xl font-bold text-gray-800 mb-5">
                            Transactions
                          </h2>

                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm border rounded-lg overflow-hidden">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="p-3 text-left">
                                    Transaction Code
                                  </th>
                                  <th className="p-3 text-left">
                                    Payment Mode
                                  </th>
                                  <th className="p-3 text-left">Date</th>
                                  <th className="p-3 text-right">Amount</th>
                                  <th className="p-3 text-center">
                                    Created By
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {record.transactionList.map((txn, index) => (
                                  <tr
                                    key={index}
                                    className="border-t hover:bg-gray-50"
                                  >
                                    <td className="p-3">
                                      {txn.transactionCode}
                                    </td>
                                    <td className="p-3">{txn.paymentMode}</td>
                                    <td className="p-3">
                                      {formatDateAndTime(txn.transactionDate)}
                                    </td>
                                    <td className="p-3 text-right font-semibold text-green-600">
                                      ₹{txn.amount}
                                    </td>
                                    <td className="p-3">
                                      {
                                        isSystemUserList.find(
                                          (user) => user?.id === txn?.createdBy,
                                        )?.name
                                      }
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Charges */}
                        <div className="bg-white rounded-2xl shadow-md border p-6">
                          <h2 className="text-xl font-bold text-gray-800 mb-5">
                            Charges Line Items
                          </h2>

                          {record.chargesList?.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm border rounded-lg overflow-hidden">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="p-3">Charge Type</th>
                                    <th className="p-3">Unit</th>
                                    <th className="p-3">Amount</th>
                                    <th className="p-3">Date</th>
                                    <th className="p-3">Note</th>
                                    {/* <th className="p-3">Created By</th> */}
                                  </tr>
                                </thead>

                                <tbody>
                                  {record.chargesList.map((charge) => (
                                    <tr
                                      key={charge.idx}
                                      className="border-t hover:bg-gray-50"
                                    >
                                      <td className="p-3">{charge.typeText}</td>
                                      <td className="p-3">
                                        {charge.unitValue}
                                      </td>
                                      <td className="p-3 font-semibold text-red-600">
                                        ₹{charge.amount}
                                      </td>
                                      <td className="p-3">
                                        {formatDateAndTime(charge.createdAt)}
                                      </td>
                                      <td className="p-3">{charge.comments}</td>
                                      {/* <td className="p-3">{charge.createdBy}</td> */}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-gray-500">
                              No Charges Available
                            </p>
                          )}
                        </div>

                        <div className="bg-white rounded-2xl shadow-md border p-6">
                          <h2 className="text-xl font-bold text-gray-800 mb-5">
                            Refunds Line Items
                          </h2>

                          {record.refundsLineItems?.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm border rounded-lg overflow-hidden">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="p-3">Refund Type</th>
                                    <th className="p-3">Unit</th>
                                    <th className="p-3">Amount</th>
                                    <th className="p-3">Date</th>
                                    <th className="p-3">Note</th>
                                    <th className="p-3">Created By</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {record.refundsLineItems.map((charge) => (
                                    <tr
                                      key={charge.idx}
                                      className="border-t hover:bg-gray-50"
                                    >
                                      <td className="p-3">{charge.typeText}</td>
                                      <td className="p-3">
                                        {charge.unitValue}
                                      </td>
                                      <td className="p-3 font-semibold text-red-600">
                                        ₹{charge.amount}
                                      </td>
                                      <td className="p-3">
                                        {formatDateAndTime(charge.createdAt)}
                                      </td>
                                      <td className="p-3">{charge.comments}</td>
                                      <td className="p-3">
                                        {charge.createdBy}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-gray-500">
                              No Refunds Available
                            </p>
                          )}
                        </div>

                        <div className="bg-white rounded-2xl shadow-md border p-6">
                          <h2 className="text-xl font-bold text-gray-800 mb-5">
                            Refunds Processed Data
                          </h2>

                          {record.refundData?.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm border rounded-lg overflow-hidden">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="p-3">Refundable Amount</th>
                                    <th className="p-3">Refund Status</th>
                                    <th className="p-3">Created At</th>
                                    <th className="p-3">Created By</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {record.refundData.map((refund) => (
                                    <tr
                                      key={refund.idx}
                                      className="border-t hover:bg-gray-50"
                                    >
                                      <td className="p-3">
                                        {refund.refundableAmount}
                                      </td>
                                      <td className="p-3">
                                        {refund.refundStatus}
                                      </td>
                                      <td className="p-3">
                                        {formatDateAndTime(refund.createdAt)}
                                      </td>
                                      <td className="p-3">
                                        {
                                          isSystemUserList.find(
                                            (user) =>
                                              user?.id === refund?.createdBy,
                                          )?.name
                                        }
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-gray-500">
                              No Charges Available
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              ))}
            {displayedRecords.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-600">
            Showing {startRecord}-{endRecord} of {totalRecords} records
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition enabled:hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition enabled:hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
          </>
        )
      }
    </div>
  );
};

export default Bookingledger;
